import type { PurchasedProduct } from "@/hooks/usePurchasedProducts";
import type { SubscriptionDeepLinkAction } from "@/lib/subscriptionActionDeepLink";
import { ChevronRight, X } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { formatSubscriptionDateForDisplay } from "./formatters";

export interface SubscriptionActionPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: SubscriptionDeepLinkAction;
  subscriptions: PurchasedProduct[];
  onSelect: (product: PurchasedProduct) => void;
}

function titleFor(action: SubscriptionDeepLinkAction): string {
  return action === "skip-next-order"
    ? "Which subscription should we skip?"
    : "Which subscription should we pause?";
}

export function SubscriptionActionPickerDialog({
  open,
  onOpenChange,
  action,
  subscriptions,
  onSelect,
}: SubscriptionActionPickerDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4 tw:max-h-[85dvh] tw:flex tw:flex-col"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              {titleFor(action)}
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
        </DialogHeader>

        <p
          className="tw:text-sm tw:my-1"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          You have {subscriptions.length} eligible subscriptions. Pick one to
          continue.
        </p>

        <div className="tw:flex tw:flex-col tw:gap-2 tw:mt-2 tw:overflow-y-auto tw:min-h-0">
          {subscriptions.map((p) => {
            const nextLabel = p.subscription_next_billing_date
              ? formatSubscriptionDateForDisplay(p.subscription_next_billing_date)
              : null;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelect(p)}
                className="tw:flex tw:items-center tw:gap-3 tw:w-full tw:text-left tw:p-2 tw:rounded-md tw:border tw:border-[#FDEDEE] tw:bg-white hover:tw:bg-[#FDEDEE]/40 tw:transition tw:cursor-pointer"
              >
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt=""
                    className="tw:size-12 tw:rounded-md tw:object-cover tw:shrink-0"
                  />
                ) : (
                  <div className="tw:size-12 tw:rounded-md tw:bg-[#FDEDEE] tw:shrink-0" />
                )}
                <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                  <span
                    className="tw:font-medium tw:text-sm tw:truncate"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    {p.title}
                  </span>
                  {nextLabel && (
                    <span className="tw:text-xs tw:text-[#523D33]/70">
                      Next delivery {nextLabel}
                    </span>
                  )}
                </div>
                <ChevronRight className="tw:size-4 tw:text-[#523D33]/60 tw:shrink-0" />
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
